import React, {useRef, useState, memo, useMemo} from "react";
import {useSelector} from "react-redux";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import {motion} from "motion/react";
import {Elements} from "@stripe/react-stripe-js";
import {loadStripe} from "@stripe/stripe-js";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faInfoCircle} from "@fortawesome/pro-regular-svg-icons/faInfoCircle";

import Alert from "@/components/Global/Alert/Alert";
import Button from "@/components/Global/Button/Button";
import Modal from "@/components/Global/Modal/Modal";
import RenderIf from "@/components/Global/RenderIf/RenderIf";
import Spinner from "@/components/Global/Spinner/Spinner";
import {DateInput} from "@/components/Global/FormFields/DateInput/DateInput";
import {useSubscriptions} from "@/hooks/useSubscriptions/useSubscriptions";
import PaymentForm from "../PaymentForm/PaymentForm";

import {closeBootstrapModal} from "@/helpers/closeBootstrapModal";

import styles from "./PreOrderModal.module.scss";
import {config} from "@/config";
import {selectSettingsMaxMonthsInAdvance} from "@/features/appSettings/appSettingsSelectors";
dayjs.extend(isBetween);

export const STEPS = {
  howOftenTest: 1,
  dispatchDate: 2,
  paymentForm: 3,
};

export default memo(function PreOrderModal({testPrice, testId, collectionMethod, modalId}) {
  const maxMonthsAvailable = useSelector(selectSettingsMaxMonthsInAdvance);
  const [dispatchDate, setDispatchDate] = useState("");
  const [step, setStep] = useState(STEPS.dispatchDate);
  const [error, setError] = useState("");

  const previousStepRef = useRef(STEPS.dispatchDate);

  const stripePromise = useMemo(() => loadStripe(import.meta.env.VITE_STRIPE_KEY), [import.meta.env.VITE_STRIPE_KEY]);
  const {isLoading, clientSecret, stripeId} = useSubscriptions({amount: testPrice});

  const minDateValid = dayjs().add(1, "d").format("YYYY-MM-DD");
  const maxDateValid = dayjs().add(maxMonthsAvailable, "months").format("YYYY-MM-DD");

  const weekDayWarning = useMemo(() => {
    if (!dayjs(dispatchDate).isValid()) return "";
    const dow = dayjs(dispatchDate).format("dddd");

    if (["Sunday", "Saturday"].includes(dow)) {
      return "Your kit will be dispatched the next working day.";
    }

    return "";
  }, [dispatchDate]);

  const handleDispatchDate = e => {
    setError("");

    const {value} = e.target;

    const minDateValid = dayjs().add(1, "d").format("YYYY-MM-DD");
    const maxDateValid = dayjs().add(maxMonthsAvailable, "months").format("YYYY-MM-DD");

    const isValid = dayjs(value).isValid();
    const isInRange = dayjs(value).isBetween(minDateValid, maxDateValid);

    if (!isValid || !isInRange) {
      setError("Please, enter a valid date");
      return;
    }

    setDispatchDate(value);
  };

  const handleSubmit = e => {
    e.preventDefault();
    setError("");

    if (!dispatchDate) {
      setError("Please, enter a valid date");
    }

    setStep(STEPS.paymentForm);
  };

  const handleGoTo = newStep => {
    previousStepRef.current = step;
    setStep(newStep);
  };

  const handleCloseModal = () => {
    closeBootstrapModal(modalId);
    setStep(STEPS.dispatchDate);
    setDispatchDate("");
    previousStepRef.current = STEPS.dispatchDate;
  };

  const forthStripeAppearance = {
    theme: "stripe",
  };

  const edgeStripeAppearance = {
    theme: "night",
    rules: {
      ".Input:focus": {
        borderColor: "#ff3d00",
        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.5), 0px 1px 6px rgba(0, 0, 0, 0.25), #ff3d00",
      },
    },
  };

  const {websiteId} = config;

  const animationDuration = 0.3;
  const exitAnimation = {opacity: 1, x: previousStepRef.current < step ? 300 : -300};
  const enterAnimation = {opacity: 0, x: previousStepRef.current > step ? -300 : 300};
  const standardAnimation = {opacity: 1, x: 0};

  return (
    <Modal id="preOrderModal" variant="bottom" onClose={handleCloseModal}>
      <div className={"p-3"}>
        <div key={"closeBtn"} className="row justify-content-end">
          <div className="col-auto mb-1">
            <button
              type="button"
              className={`btn-close ${styles.closeBtn}`}
              aria-label="Close"
              onClick={handleCloseModal}></button>
          </div>
        </div>

        <RenderIf isTrue={step === STEPS.howOftenTest}>
          <motion.section
            className="row"
            initial={enterAnimation}
            animate={standardAnimation}
            exit={exitAnimation}
            transition={{duration: animationDuration}}>
            <div className="col-12">
              <h2>How often should I test?</h2>
              <p className="lh-base">
                We recommend a follow up test 3 months after your first test, followed by a test every 6 months
                depending on your results, but its up to you!
              </p>
            </div>
            <div className="col-12">
              <Button variant="link-default" className="w-100" onClick={() => handleGoTo(STEPS.dispatchDate)}>
                Go back
              </Button>
            </div>
          </motion.section>
        </RenderIf>

        <RenderIf isTrue={step === STEPS.dispatchDate}>
          <motion.section
            className="row"
            initial={enterAnimation}
            animate={standardAnimation}
            exit={exitAnimation}
            transition={{duration: animationDuration}}>
            <div className="col-12">
              <h2>When would you like your next test kit to arrive?</h2>
              <p className="lh-base">
                Select in advance when you'd like your next test to arrive.
                <Button
                  variant="link"
                  className="fw-normal fst-italic ps-1 pe-0 py-0 ls-normal"
                  onClick={() => handleGoTo(STEPS.howOftenTest)}>
                  How often should I test?
                </Button>
              </p>
              <p className="lh-base">
                Don't worry, you won't be charged until the test kit is dispatched, and you can cancel anytime.
              </p>
              <RenderIf isTrue={weekDayWarning}>
                <Alert variant="info" showShadow={false} className="d-flex gap-2">
                  <div className="col-auto">
                    <FontAwesomeIcon icon={faInfoCircle} className="fa-fw" size="lg" />
                  </div>
                  <div className="col">
                    <p className="lh-base mb-0">{weekDayWarning}</p>
                  </div>
                </Alert>
              </RenderIf>
            </div>

            <form onSubmit={handleSubmit} className="col-12">
              <DateInput
                min={minDateValid}
                max={maxDateValid}
                value={dispatchDate}
                onChange={handleDispatchDate}
                isError={Boolean(error)}
                errorMsg={error}
                className="mt-2"
                aria-label="dispatch-date"
                required
              />

              <Button type="submit" className="mt-4 w-100 py-3">
                Continue
              </Button>

              <Button
                type="button"
                variant="link-default"
                className="mt-3 w-100"
                data-bs-dismiss="modal"
                aria-label="Close">
                Cancel
              </Button>
            </form>
          </motion.section>
        </RenderIf>

        <RenderIf isTrue={step === STEPS.paymentForm}>
          <motion.section
            className="row"
            initial={enterAnimation}
            animate={standardAnimation}
            exit={exitAnimation}
            transition={{duration: animationDuration}}>
            <div className="col-12">
              <RenderIf isTrue={!isLoading && clientSecret?.length > 0}>
                <Elements
                  stripe={stripePromise}
                  options={{clientSecret, appearance: websiteId == 2 ? edgeStripeAppearance : forthStripeAppearance}}>
                  <PaymentForm
                    action="pre-book"
                    handleGoTo={handleGoTo}
                    dispatchDate={dispatchDate}
                    testPrice={testPrice}
                    testId={testId}
                    collectionMethod={collectionMethod}
                    stripeId={stripeId}
                    handleCloseModal={handleCloseModal}
                    handleBack={() => handleGoTo(STEPS.dispatchDate)}
                  />
                </Elements>
              </RenderIf>
              <RenderIf isTrue={isLoading || clientSecret?.length === 0}>
                <div className="row my-5 justify-content-center align-items-center">
                  <div className="col-12 text-center">
                    <Spinner className="me-2" /> <span>Loading...</span>
                  </div>
                </div>
              </RenderIf>
            </div>
          </motion.section>
        </RenderIf>
      </div>
    </Modal>
  );
});
